
import { useNavigate, useLocation } from "react-router-dom";
import { cn } from "@/lib/utils";
import { Button } from "@/components/ui/button";
import {
  CreditCard,
  FileText,
  Home,
  Building,
  Settings,
  MessageSquare,
  TrendingUp,
  CheckSquare,
  LayoutDashboard,
  User,
  Menu,
  X,
  LogOut,
} from "lucide-react";
import { useState, useEffect } from "react";
import { useSessionContext } from "@supabase/auth-helpers-react";
import { supabase } from "@/integrations/supabase/client";
import { useToast } from "@/components/ui/use-toast";
import { borrowerNavigation } from "../navigation/configs/borrowerNavigation";

export function BorrowerSidebar() {
  const navigate = useNavigate();
  const location = useLocation();
  const { toast } = useToast();
  const { session } = useSessionContext();
  // Use localStorage to remember sidebar state between sessions
  const [isCollapsed, setIsCollapsed] = useState(() => {
    const savedState = localStorage.getItem("sidebar-collapsed");
    return savedState ? JSON.parse(savedState) : false;
  });
  
  // Update localStorage when sidebar state changes
  useEffect(() => {
    localStorage.setItem("sidebar-collapsed", JSON.stringify(isCollapsed));
  }, [isCollapsed]);
  
  const isActivePath = (path: string) => {
    if (path === "/borrower/dashboard") {
      return location.pathname === path;
    }
    return location.pathname.startsWith(path);
  };

  const handleSignOut = async () => {
    try {
      const { error } = await supabase.auth.signOut();
      if (error) {
        console.error('Error signing out:', error);
        toast({
          title: "Error",
          description: "Failed to sign out. Please try again.",
          variant: "destructive",
        });
        return;
      }
      
      toast({
        title: "Success",
        description: "You have been signed out successfully.",
      });
      navigate('/');
    } catch (error) {
      console.error('Unexpected error during sign out:', error);
      toast({
        title: "Error",
        description: "An unexpected error occurred. Please try again.",
        variant: "destructive",
      });
    }
  };

  const handleNavigation = (path: string) => {
    navigate(path);
  };

  return (
    <div 
      className={cn(
        "bg-sidebar h-screen border-r border-sidebar-border transition-all duration-300 flex flex-col fixed top-0 left-0 z-40",
        isCollapsed ? "w-[70px]" : "w-[240px]"
      )}
      style={{ position: 'fixed', overflowY: 'auto' }}
    >
      {/* Sidebar Header */}
      <div className="h-16 flex items-center px-4 border-b border-sidebar-border justify-between">
        {!isCollapsed && (
          <div className="flex items-center gap-2">
            <CreditCard className="h-5 w-5 text-primary" />
            <span className="font-bold text-lg text-primary">Obotan</span>
          </div>
        )}
        <Button 
          variant="ghost" 
          size="icon" 
          onClick={() => setIsCollapsed(!isCollapsed)}
          className="text-sidebar-muted hover:text-sidebar-foreground transition-colors"
          aria-label={isCollapsed ? "Expand sidebar" : "Collapse sidebar"}
        >
          {isCollapsed ? <Menu className="h-5 w-5" /> : <X className="h-5 w-5" />}
        </Button>
      </div>

      {/* Sidebar Content */}
      <div className="flex-1 overflow-y-auto p-3 space-y-2">
        {borrowerNavigation.map((item) => (
          <Button
            key={item.id}
            variant="ghost"
            className={cn(
              "w-full justify-start px-3 py-2 rounded-md text-sidebar-muted hover:text-sidebar-foreground hover:bg-sidebar-accent/70 transition-all duration-200",
              isActivePath(item.href) && "bg-sidebar-accent text-sidebar-foreground font-medium shadow-sm",
              isCollapsed ? "justify-center px-2" : "justify-start px-3"
            )}
            onClick={() => handleNavigation(item.href)}
          >
            <item.icon className={cn("h-5 w-5", isCollapsed ? "mr-0" : "mr-2")} />
            {!isCollapsed && <span>{item.name}</span>}
          </Button>
        ))}
      </div>

      {/* Sidebar Footer */}
      <div className="p-3 border-t border-sidebar-border">
        <Button
          variant="ghost"
          className={cn(
            "w-full justify-start px-3 py-2 text-destructive hover:text-destructive/90 hover:bg-sidebar-accent/70 transition-all duration-200",
            isCollapsed ? "justify-center px-2" : "justify-start px-3"
          )}
          onClick={handleSignOut}
        >
          <LogOut className={cn("h-5 w-5", isCollapsed ? "mr-0" : "mr-2")} />
          {!isCollapsed && <span>Sign Out</span>}
        </Button>
      </div>
    </div>
  );
}
